/**
 * Chase Reason Engine
 *
 * Pure ranking core answering: "why am I being shown this contact today?"
 * No network, no Supabase, no Date.now() — the caller always supplies `now`.
 *
 * This module returns machine-readable codes only. English copy for each
 * code lives in the component layer, kept out of this module so an
 * English-consistency scanner over rendered text stays trivially satisfiable
 * and this module stays independently testable.
 */

import { isActiveDashboardContact } from './dashboard.js';
import type { Contact } from './types.js';

export type ChaseReasonCode =
  | 'dropped_inbound' // they replied and we owe an answer (action_state === 'response_received')
  | 'overdue' // due_date strictly before today
  | 'due_today'
  | 'waiting_too_long' // action_state === 'waiting_response' and last_contact_date is far back
  | 'no_next_action' // active contact with no next_action recorded
  | 'no_due_date'; // active contact with no due_date

export interface ChaseReason {
  code: ChaseReasonCode;
  days?: number;
  weight: number;
}

/** Days a `waiting_response` contact can go untouched before it counts as "waiting too long". */
export const WAITING_TOO_LONG_THRESHOLD_DAYS = 7;

// Highest to lowest: a contact who replied and is being ignored outranks a
// self-imposed deadline — that is the product judgement, encoded here.
const REASON_WEIGHTS: Record<ChaseReasonCode, number> = {
  dropped_inbound: 100,
  overdue: 80,
  due_today: 60,
  waiting_too_long: 40,
  no_next_action: 20,
  no_due_date: 10,
};

// Mirrors the date-handling convention in ./dashboard.ts: dates are
// YYYY-MM-DD strings, invalid/impossible ones must resolve to absent (never
// epoch 0, never NaN).
function parseDate(value: string | undefined): Date | null {
  if (!value) return null;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const parsed = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);

  if (
    dateOnlyMatch
    && (
      parsed.getFullYear() !== Number(dateOnlyMatch[1])
      || parsed.getMonth() !== Number(dateOnlyMatch[2]) - 1
      || parsed.getDate() !== Number(dateOnlyMatch[3])
    )
  ) {
    return null;
  }

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function startOfDay(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  return result;
}

function daysBetween(earlier: Date, later: Date): number {
  return Math.round(
    (startOfDay(later).getTime() - startOfDay(earlier).getTime()) / 86_400_000,
  );
}

export const getChaseReasons = (contact: Contact, now: Date): ChaseReason[] => {
  if (contact.completed_at) return [];

  const reasons: ChaseReason[] = [];

  if (contact.action_state === 'response_received') {
    reasons.push({ code: 'dropped_inbound', weight: REASON_WEIGHTS.dropped_inbound });
  }

  const dueDate = parseDate(contact.due_date);
  if (dueDate) {
    const daysOverdue = daysBetween(dueDate, now);
    if (daysOverdue > 0) {
      reasons.push({ code: 'overdue', days: daysOverdue, weight: REASON_WEIGHTS.overdue });
    } else if (daysOverdue === 0) {
      reasons.push({ code: 'due_today', weight: REASON_WEIGHTS.due_today });
    }
  }

  if (contact.action_state === 'waiting_response') {
    const lastContactDate = parseDate(contact.last_contact_date);
    if (lastContactDate) {
      const daysWaiting = daysBetween(lastContactDate, now);
      if (daysWaiting >= WAITING_TOO_LONG_THRESHOLD_DAYS) {
        reasons.push({
          code: 'waiting_too_long',
          days: daysWaiting,
          weight: REASON_WEIGHTS.waiting_too_long,
        });
      }
    }
  }

  if (isActiveDashboardContact(contact)) {
    if (!contact.next_action) {
      reasons.push({ code: 'no_next_action', weight: REASON_WEIGHTS.no_next_action });
    }
    if (!dueDate) {
      reasons.push({ code: 'no_due_date', weight: REASON_WEIGHTS.no_due_date });
    }
  }

  return reasons.sort((left, right) => right.weight - left.weight);
};

export const getPrimaryChaseReason = (contact: Contact, now: Date): ChaseReason | null =>
  getChaseReasons(contact, now)[0] ?? null;
